import React, { useState, useEffect } from 'react';
import { loanProductsApi, api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Package, Plus, Edit, FileText, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const LOAN_CATEGORIES = [
  { value: 'home_loan', label: 'Home Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'vehicle_asset_finance', label: 'Vehicle & Asset Finance' },
  { value: 'student_loan', label: 'Student Loan' },
  { value: 'sme_loan', label: 'SME Loan' },
  { value: 'agricultural', label: 'Agricultural' },
  { value: 'group_loan', label: 'Group Loan' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS = {
  home_loan: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  personal_loan: 'bg-teal-50 text-teal-700 border-teal-200',
  vehicle_asset_finance: 'bg-amber-50 text-amber-700 border-amber-200',
  student_loan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  sme_loan: 'bg-orange-50 text-orange-700 border-orange-200',
  agricultural: 'bg-lime-50 text-lime-700 border-lime-200',
  group_loan: 'bg-purple-50 text-purple-700 border-purple-200',
  other: 'bg-gray-50 text-gray-700 border-gray-200',
};

const LoanProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', description: '', category: 'other',
    min_amount: '', max_amount: '',
    min_term_months: '', max_term_months: '', interest_rate: '',
    interest_rate_type: 'flat', interest_rate_mode: 'fixed', sarb_spread: '',
    repayment_frequency: 'monthly',
    grace_period_days: 0, penalty_rate: 0, requires_collateral: false, is_group_loan: false,
    min_credit_score: '', enable_statements: true, fund_id: '', cli_scheme_id: '',
    eligible_cli_scheme_ids: [], allow_byo_cli: true,
  });
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [cliSchemes, setCliSchemes] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const contractInputRef = React.useRef(null);
  const { user } = useAuth();

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [prodRes, fundsRes, cliRes] = await Promise.all([
        loanProductsApi.list(),
        api.get('/funds').catch(() => ({ data: [] })),
        api.get('/insurance/schemes').catch(() => ({ data: [] })),
      ]);
      setProducts(prodRes.data);
      setFunds(fundsRes.data || []);
      setCliSchemes((cliRes.data || []).filter(s => s.is_active));
    }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    try {
      await loanProductsApi.create({
        ...form,
        min_amount: parseFloat(form.min_amount) || 0,
        max_amount: parseFloat(form.max_amount) || 0,
        min_term_months: parseInt(form.min_term_months) || 0,
        max_term_months: parseInt(form.max_term_months) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        grace_period_days: parseInt(form.grace_period_days) || 0,
        penalty_rate: parseFloat(form.penalty_rate) || 0,
        sarb_spread: form.interest_rate_mode === 'floating' ? (parseFloat(form.sarb_spread) || 0) : null,
        min_credit_score: form.min_credit_score ? parseInt(form.min_credit_score) : null,
        is_group_loan: form.category === 'group_loan' || form.is_group_loan,
        fund_id: form.fund_id || null,
        cli_scheme_id: form.cli_scheme_id || null,
        eligible_cli_scheme_ids: form.eligible_cli_scheme_ids || [],
        allow_byo_cli: form.allow_byo_cli !== false,
        covenants: {
          dscr: form.covenant_dscr ? parseFloat(form.covenant_dscr) : null,
          ltv: form.covenant_ltv ? parseFloat(form.covenant_ltv) : null,
          debt_to_equity: form.covenant_dte ? parseFloat(form.covenant_dte) : null,
          interest_coverage: form.covenant_icr ? parseFloat(form.covenant_icr) : null,
        },
        fees: []
      });
      toast.success('Loan product created');
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const openEditModal = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name || '', description: p.description || '', category: p.category || 'other',
      min_amount: p.min_amount || '', max_amount: p.max_amount || '',
      min_term_months: p.min_term_months || '', max_term_months: p.max_term_months || '',
      interest_rate: p.interest_rate || '', interest_rate_type: p.interest_rate_type || 'flat',
      interest_rate_mode: p.interest_rate_mode || 'fixed', sarb_spread: p.sarb_spread || '',
      repayment_frequency: p.repayment_frequency || 'monthly',
      grace_period_days: p.grace_period_days || 0, penalty_rate: p.penalty_rate || 0,
      requires_collateral: p.requires_collateral || false,
      min_credit_score: p.min_credit_score ?? '', enable_statements: p.enable_statements !== false,
      effective_date: new Date().toISOString().slice(0, 10),
      covenant_dscr: p.covenants?.dscr || '',
      covenant_ltv: p.covenants?.ltv || '',
      covenant_dte: p.covenants?.debt_to_equity || '',
      covenant_icr: p.covenants?.interest_coverage || '',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editForm,
        min_amount: parseFloat(editForm.min_amount) || undefined,
        max_amount: parseFloat(editForm.max_amount) || undefined,
        min_term_months: parseInt(editForm.min_term_months) || undefined,
        max_term_months: parseInt(editForm.max_term_months) || undefined,
        interest_rate: parseFloat(editForm.interest_rate) || undefined,
        grace_period_days: parseInt(editForm.grace_period_days) || 0,
        penalty_rate: parseFloat(editForm.penalty_rate) || 0,
        sarb_spread: editForm.interest_rate_mode === 'floating' ? (parseFloat(editForm.sarb_spread) || 0) : undefined,
        min_credit_score: editForm.min_credit_score !== '' ? parseInt(editForm.min_credit_score) : null,
      };
      const res = await api.put(`/loan-products/${editProduct.id}`, payload);
      if (res.data.status === 'pending') {
        toast.success('Product change submitted for checker approval');
      } else {
        toast.success('Product updated');
      }
      setShowEditModal(false);
      load();
      loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/pending-product-changes');
      setPendingChanges(res.data);
    } catch (e) { /* ignore if no access */ }
  };

  const handleApprove = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/approve`, { comments: '' });
      toast.success('Product change approved');
      loadPending();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/reject`, { comments: '' });
      toast.success('Product change rejected');
      loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const handleContractUpload = async (productId) => {
    if (!contractFile) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const b64 = e.target.result.split(',')[1];
        await api.post(`/loan-products/${productId}/contract/upload`, {
          file_data: b64,
          file_name: contractFile.name,
          file_type: contractFile.type,
        });
        toast.success('Contract uploaded');
        setContractFile(null);
        load();
      };
      reader.readAsDataURL(contractFile);
    } catch (e) { toast.error('Failed to upload contract'); }
  };

  useEffect(() => { loadPending(); }, []);

  return (
    <div className="space-y-6 fade-in" data-testid="loan-products-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Loan Products</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PRODUCT CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Loan Products</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-loan-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Loan Product
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Code</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Amount Range</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Term</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Interest</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Rate Mode</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Min Score</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            products.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">No loan products. Click "Create Loan Product" to add one.</td></tr> :
            products.map(p => {
              const catLabel = LOAN_CATEGORIES.find(c => c.value === p.category)?.label || (p.category || 'Other').replace(/_/g, ' ');
              const catColor = CATEGORY_COLORS[p.category] || CATEGORY_COLORS.other;
              return (
              <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`lp-row-${p.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{p.name}</div><div className="text-xs text-gray-400">{p.description?.substring(0, 50)}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded border ${catColor}`} data-testid={`lp-cat-${p.id}`}>{catLabel}</span></td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{p.code}</td>
                <td className="px-4 py-3 text-sm text-gray-600">R {p.min_amount?.toLocaleString()} - R {p.max_amount?.toLocaleString()}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.min_term_months}-{p.max_term_months} months</td>
                <td className="px-4 py-3 text-sm font-medium">{p.interest_rate}% ({p.interest_rate_type})</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded ${p.interest_rate_mode === 'floating' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`} data-testid={`lp-mode-${p.id}`}>
                    {p.interest_rate_mode === 'floating' ? 'Floating (SARB)' : 'Fixed'}
                    {p.interest_rate_mode === 'floating' && p.sarb_spread != null && <span className="ml-1 font-mono">+{p.sarb_spread}%</span>}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.min_credit_score != null ? p.min_credit_score : '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${p.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.is_active !== false ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500 hover:text-[#1f1f1f]" onClick={() => openEditModal(p)} data-testid={`lp-edit-${p.id}`}>Edit</Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="lp-form-desc">
          <DialogHeader><DialogTitle>Create Loan Product</DialogTitle></DialogHeader>
          <p id="lp-form-desc" className="sr-only">Form to create a loan product</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="lp-name" /></div>
              <div><Label>Short Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} data-testid="lp-code" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v, is_group_loan: v === 'group_loan'})}>
                  <SelectTrigger data-testid="lp-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {LOAN_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="lp-desc" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Min Amount (ZAR) *</Label><Input type="number" value={form.min_amount} onChange={e => setForm({...form, min_amount: e.target.value})} data-testid="lp-min-amt" /></div>
              <div><Label>Max Amount (ZAR) *</Label><Input type="number" value={form.max_amount} onChange={e => setForm({...form, max_amount: e.target.value})} data-testid="lp-max-amt" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Min Term (months) *</Label><Input type="number" value={form.min_term_months} onChange={e => setForm({...form, min_term_months: e.target.value})} data-testid="lp-min-term" /></div>
              <div><Label>Max Term (months) *</Label><Input type="number" value={form.max_term_months} onChange={e => setForm({...form, max_term_months: e.target.value})} data-testid="lp-max-term" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Interest Rate (%) *</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} data-testid="lp-rate" /></div>
              <div><Label>Rate Type</Label>
                <Select value={form.interest_rate_type} onValueChange={v => setForm({...form, interest_rate_type: v})}>
                  <SelectTrigger data-testid="lp-rate-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="flat">Flat</SelectItem><SelectItem value="declining">Declining Balance</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Repayment</Label>
                <Select value={form.repayment_frequency} onValueChange={v => setForm({...form, repayment_frequency: v})}>
                  <SelectTrigger data-testid="lp-freq"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Bi-weekly</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {/* Rate Mode: Fixed vs Floating */}
            <div className="p-4 border border-gray-200 bg-gray-50 space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Interest Rate Mode</Label>
              <div className="flex gap-4">
                <label className={`flex items-center gap-2 cursor-pointer p-3 border flex-1 transition-colors ${form.interest_rate_mode === 'fixed' ? 'border-[#1f1f1f] bg-white' : 'border-gray-200'}`}>
                  <input type="radio" name="rate_mode" value="fixed" checked={form.interest_rate_mode === 'fixed'} onChange={() => setForm({...form, interest_rate_mode: 'fixed'})} data-testid="lp-mode-fixed" />
                  <div>
                    <p className="text-sm font-medium text-[#1f1f1f]">Fixed Rate</p>
                    <p className="text-xs text-gray-500">Rate stays constant for loan lifetime</p>
                  </div>
                </label>
                <label className={`flex items-center gap-2 cursor-pointer p-3 border flex-1 transition-colors ${form.interest_rate_mode === 'floating' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input type="radio" name="rate_mode" value="floating" checked={form.interest_rate_mode === 'floating'} onChange={() => setForm({...form, interest_rate_mode: 'floating'})} data-testid="lp-mode-floating" />
                  <div>
                    <p className="text-sm font-medium text-blue-700">Floating Rate (SARB-Linked)</p>
                    <p className="text-xs text-gray-500">Rate = SARB Repo + Spread, adjusts with repo changes</p>
                  </div>
                </label>
              </div>
              {form.interest_rate_mode === 'floating' && (
                <div className="pt-2">
                  <Label>Default SARB Spread (%)</Label>
                  <Input type="number" step="0.5" value={form.sarb_spread} onChange={e => setForm({...form, sarb_spread: e.target.value})} placeholder="e.g., 5.0" data-testid="lp-sarb-spread" />
                  <p className="text-xs text-gray-400 mt-1">This spread is added to the SARB repo rate. Actual spread may be adjusted by client credit score tier.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Min Credit Score</Label><Input type="number" value={form.min_credit_score} onChange={e => setForm({...form, min_credit_score: e.target.value})} placeholder="e.g. 600" data-testid="lp-min-score" /><p className="text-xs text-gray-400 mt-1">Minimum account-level score required</p></div>
              <div><Label>Grace Period (days)</Label><Input type="number" value={form.grace_period_days} onChange={e => setForm({...form, grace_period_days: e.target.value})} data-testid="lp-grace" /></div>
              <div><Label>Penalty Rate (%)</Label><Input type="number" step="0.01" value={form.penalty_rate} onChange={e => setForm({...form, penalty_rate: e.target.value})} data-testid="lp-penalty" /></div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.requires_collateral} onCheckedChange={v => setForm({...form, requires_collateral: v})} /><Label>Requires Collateral</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.enable_statements} onCheckedChange={v => setForm({...form, enable_statements: v})} /><Label>Enable Statements</Label></div>
              {form.category !== 'group_loan' && <div className="flex items-center gap-2"><Switch checked={form.is_group_loan} onCheckedChange={v => setForm({...form, is_group_loan: v})} /><Label>Group Loan</Label></div>}
            </div>
            <div>
              <Label>Fund Source</Label>
              <Select value={form.fund_id || 'none'} onValueChange={v => setForm({...form, fund_id: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="lp-fund"><SelectValue placeholder="No fund (general portfolio)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Fund (General Portfolio)</SelectItem>
                  {funds.map(f => <SelectItem key={f.id} value={f.id}>{f.name} — R{(f.available || 0).toLocaleString()} available</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400 mt-1">Loans under this product will be financed from the selected fund. GL entries route through fund-mapped accounts.</p>
            </div>
            <div className="p-4 border border-gray-200 bg-amber-50/40 space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Credit Life Insurance</Label>
              <div>
                <Label className="text-xs">Primary Scheme (auto-issued on disbursement if mandatory)</Label>
                <Select value={form.cli_scheme_id || 'none'} onValueChange={v => setForm({...form, cli_scheme_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="lp-cli-scheme"><SelectValue placeholder="No CLI" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No primary CLI</SelectItem>
                    {cliSchemes.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} — {s.insurer_name} (R{Number(s.premium_rate_per_1000).toFixed(2)}/R1000)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Additional Eligible Schemes (borrower may choose)</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="lp-eligible-schemes">
                  {cliSchemes.map(s => {
                    const checked = (form.eligible_cli_scheme_ids || []).includes(s.id);
                    const isPrimary = form.cli_scheme_id === s.id;
                    return (
                      <button key={s.id} type="button"
                        onClick={() => {
                          const current = form.eligible_cli_scheme_ids || [];
                          const next = checked ? current.filter(x => x !== s.id) : [...current, s.id];
                          setForm({...form, eligible_cli_scheme_ids: next});
                        }}
                        disabled={isPrimary}
                        className={`text-[11px] px-2 py-1 border ${isPrimary ? 'bg-[#C5A059] text-white border-[#C5A059]' : checked ? 'bg-[#1f1f1f] text-white border-[#1f1f1f]' : 'bg-white text-gray-600 border-gray-300'} ${isPrimary ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                        title={isPrimary ? 'Already set as Primary' : checked ? 'Click to remove' : 'Click to add'}>
                        {isPrimary ? '★ ' : checked ? '✓ ' : ''}{s.name} <span className="text-[10px] opacity-75">({s.insurer_name})</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">★ = primary; ✓ = eligible-but-not-primary. Borrowers can choose any of these at issuance.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.allow_byo_cli !== false} onCheckedChange={v => setForm({...form, allow_byo_cli: v})} data-testid="lp-allow-byo" />
                <Label className="text-xs">Allow Bring-Your-Own (client uses external insurer — NCA right)</Label>
              </div>
            </div>
            {/* Covenants Section */}
            <div className="p-4 border border-gray-200 bg-gray-50 space-y-3" data-testid="lp-covenants-section">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Loan Covenants (Health Checks)</Label>
              <p className="text-xs text-gray-400">Define covenant thresholds for this product. Leave blank or set to 0 to disable a covenant.</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">DSCR Minimum (e.g. 1.25)</Label><Input type="number" step="0.05" value={form.covenant_dscr || ''} onChange={e => setForm({...form, covenant_dscr: e.target.value})} placeholder="1.25" data-testid="lp-cov-dscr" /></div>
                <div><Label className="text-xs">LTV Maximum % (e.g. 80)</Label><Input type="number" step="1" value={form.covenant_ltv || ''} onChange={e => setForm({...form, covenant_ltv: e.target.value})} placeholder="80" data-testid="lp-cov-ltv" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Debt-to-Equity Maximum (e.g. 3.0)</Label><Input type="number" step="0.1" value={form.covenant_dte || ''} onChange={e => setForm({...form, covenant_dte: e.target.value})} placeholder="3.0" data-testid="lp-cov-dte" /></div>
                <div><Label className="text-xs">Interest Coverage Minimum (e.g. 2.0)</Label><Input type="number" step="0.1" value={form.covenant_icr || ''} onChange={e => setForm({...form, covenant_icr: e.target.value})} placeholder="2.0" data-testid="lp-cov-icr" /></div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="lp-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="edit-form-desc">
          <DialogHeader><DialogTitle>Edit: {editProduct?.name}</DialogTitle></DialogHeader>
          <p id="edit-form-desc" className="sr-only">Form to edit a loan product</p>
          {editProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {user?.role === 'admin' ? 'Admin: changes apply immediately.' : 'Changes will be submitted for checker approval.'}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Product Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div><Label>Category</Label>
                  <Select value={editForm.category} onValueChange={v => setEditForm({...editForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LOAN_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={editForm.interest_rate} onChange={e => setEditForm({...editForm, interest_rate: e.target.value})} data-testid="edit-rate" /></div>
                <div><Label>Min Credit Score</Label><Input type="number" value={editForm.min_credit_score} onChange={e => setEditForm({...editForm, min_credit_score: e.target.value})} /></div>
                <div><Label>Effective Date</Label><Input type="date" value={editForm.effective_date} onChange={e => setEditForm({...editForm, effective_date: e.target.value})} data-testid="edit-effective-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Min Amount</Label><Input type="number" value={editForm.min_amount} onChange={e => setEditForm({...editForm, min_amount: e.target.value})} /></div>
                <div><Label>Max Amount</Label><Input type="number" value={editForm.max_amount} onChange={e => setEditForm({...editForm, max_amount: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Min Term (months)</Label><Input type="number" value={editForm.min_term_months} onChange={e => setEditForm({...editForm, min_term_months: e.target.value})} /></div>
                <div><Label>Max Term (months)</Label><Input type="number" value={editForm.max_term_months} onChange={e => setEditForm({...editForm, max_term_months: e.target.value})} /></div>
              </div>
              <div className="flex gap-6">
                <div className="flex items-center gap-2"><Switch checked={editForm.requires_collateral} onCheckedChange={v => setEditForm({...editForm, requires_collateral: v})} /><Label>Collateral</Label></div>
                <div className="flex items-center gap-2"><Switch checked={editForm.enable_statements} onCheckedChange={v => setEditForm({...editForm, enable_statements: v})} /><Label>Statements</Label></div>
              </div>

              {/* Contract Upload */}
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <div className="p-3 border border-gray-200 bg-gray-50 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">T&C / Contract Document</Label>
                  {editProduct.contract_file_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <FileText className="w-3.5 h-3.5" />
                      <span>Current: {editProduct.contract_file_name} (v{editProduct.contract_version})</span>
                      <Button size="sm" variant="ghost" className="h-5 text-xs text-blue-600" onClick={async () => { try { const res = await api.get(`/loan-products/${editProduct.id}/contract/download`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = editProduct.contract_file_name; a.click(); } catch(e) { toast.error('No contract available'); }}}>
                        Download
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Input type="file" accept=".pdf,.doc,.docx" ref={contractInputRef} onChange={e => setContractFile(e.target.files?.[0])} className="text-xs" />
                    <Button size="sm" disabled={!contractFile} onClick={() => handleContractUpload(editProduct.id)} className="text-xs bg-[#1f1f1f] text-white" data-testid="upload-contract-btn">Upload</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} className="bg-[#1f1f1f] text-white" data-testid="edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Product Changes Queue */}
      {pendingChanges.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
        <div className="bg-white border border-amber-200 p-4" data-testid="pending-changes-section">
          <h3 className="text-sm font-bold text-[#1f1f1f] mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />Pending Product Changes ({pendingChanges.length})
          </h3>
          <div className="space-y-2">
            {pendingChanges.map(pc => (
              <div key={pc.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100" data-testid={`pending-${pc.id}`}>
                <div>
                  <p className="text-sm font-medium">{pc.product_name} ({pc.product_code})</p>
                  <p className="text-xs text-gray-500">By: {pc.maker_name} ({pc.maker_role}) | Effective: {pc.effective_date?.slice(0,10)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Changes: {Object.entries(pc.changes || {}).map(([k,v]) => `${k}: ${v}`).join(', ')}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(pc.id)} className="bg-green-600 text-white text-xs" data-testid={`approve-${pc.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(pc.id)} className="text-xs text-red-600 border-red-200" data-testid={`reject-${pc.id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanProductsPage;
